/**
 * Money and date formatting helpers. ALL monetary fields on the wire are
 * integer minor units (E2-API-REFERENCE.md "Money") -- never float-math an
 * aggregate client-side; the server computes everything, this file only
 * renders what it's given.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DEFAULT_CURRENCY, formatCurrency } from '@1nl/core';

import { formatWithCodeSuffix } from './currency/formats.ts';

dayjs.extend(utc);

/**
 * The app-wide money formatter (v1 parity, "K1,234.56"): PGK -- or a null
 * code (pre-first-sync, assumed PGK-shaped) -- renders with the K symbol via
 * DEFAULT_CURRENCY; any other code renders "1,234.56 XYZ" (grouped digits, no
 * invented symbol). Dashboard figures reach it via MoneyText; the reports
 * summary/table call it directly.
 */
export function formatMoney(minor: number, currencyCode: string | null): string {
  if (currencyCode === null || currencyCode === DEFAULT_CURRENCY.code) {
    return formatCurrency(minor, DEFAULT_CURRENCY);
  }
  return formatWithCodeSuffix(minor, currencyCode);
}

/** "YYYY-MM-DD" from an ISO date/date-time string. Rendered in UTC so the
 *  result never shifts with the viewer's local timezone (submission_date is
 *  a calendar date, not a moment). */
export function formatDate(iso: string): string {
  return dayjs.utc(iso).format('YYYY-MM-DD');
}

/** "YYYY-MM-DD HH:mm" (UTC) from an ISO date-time string. */
export function formatDateTime(iso: string): string {
  return dayjs.utc(iso).format('YYYY-MM-DD HH:mm');
}
